import React, { useState } from 'react'
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { BasicModal, Tabs } from '@life-line-lab/components-react'
// antd
import { CloseCircleOutlined } from '@ant-design/icons'
import { Row } from 'antd';
// shared
import ModalContent from 'Components/shared/modal-content';
// tab components
import EmploymentDetails from './tab-contents/employment-details';
import UserDetails from './tab-contents/user-details';
import PaymentDetails from './tab-contents/payment-details';
import EmergencyContacts from './tab-contents/emergency-contacts';
import Attachments from './tab-contents/attachments';
import { UserProfileTabFooter } from './tab-footer';
// helpers
import { encode } from 'Helpers/index';
// types
import { AccountRole, ModalTranslationContent, ModalType, Statuses } from 'Types/common.types';
import { FetchedUserData, UserProfileCategoriesTab, UserRoleToAssign } from 'Types/user.types';
// scss
import "./user-profile.scss";

interface UserProfileProps {
    canEdit:boolean,
    canDelete:boolean,
    onBack:()=>void;
    onDeactivateUser:(profileID:string,toDelete:boolean,reason:string)=>void;
    userDetails:FetchedUserData;
}

const UserProfile:React.FC<UserProfileProps> = ({
    canEdit,
    canDelete,
    onBack,
    onDeactivateUser,
    userDetails,
}) => {
    const { t } = useTranslation();
    const history = useHistory();

    // add common properties used by all tab components to `commonTabsProps`
    const commonTabsProps = {
        userDetails,
    };
    const initialModal:ModalType<Partial<FetchedUserData>> = {
        isOpen: false, record: {}, type: "approve"
    };
    const editFormType = [
        UserProfileCategoriesTab.USER_DETAILS,
        UserProfileCategoriesTab.EMPLOYMENT_DETAILS,
        UserProfileCategoriesTab.PAYMENT_DETAILS,
        UserProfileCategoriesTab.EMERGENCY_CONTACTS,
        UserProfileCategoriesTab.ATTACHMENTS,
        // if more steps are needed, add steps name here, so that it will direct to the same step component on edit.
    ];

    // modify number of tabs shown in `tabs`
    const tabs = [
        {
            tabKey: '0',
            label: t("tab.userDetails"),
            component: <UserDetails
                {...commonTabsProps}
            />
        },
        {
            tabKey: '1',
            label: t("tab.employmentDetails"),
            component: <EmploymentDetails
                {...commonTabsProps}
            />
        },
        {
            tabKey: '2',
            label: t("tab.paymentDetails"),
            component: <PaymentDetails
                {...commonTabsProps}
            />
        },
        {
            tabKey: '3',
            label: t("tab.emergencyContactDetails"),
            component: <EmergencyContacts
                {...commonTabsProps}
            />
        },
        {
            tabKey: '4',
            label: t("tab.attachments"),
            component: <Attachments
                {...commonTabsProps}
            />
        },
    ];

    const [tabActiveKey, setTabActiveKey] = useState<string>("0");
    const [modal, setModal] = useState<ModalType<Partial<FetchedUserData>>>(initialModal);

    const handleTabClick = (key: string) => {
        setTabActiveKey(key);
    }

    const handleEditUser = () => {
        if (userDetails.profile?.id && userDetails.userRole?.orgID) {
            const userOrgID = encode(userDetails.userRole.orgID);
            const userProfileID = encode(userDetails.profile.id);
            history.push(`/user-management/user/${editFormType[parseInt(tabActiveKey)].toLowerCase()}/${userProfileID}/${userOrgID}/edit`);
        }
    }

    // ? write as a helper function
    const handleModalTranslation = () : ModalTranslationContent => {
        if (modal.type === Statuses.DEACTIVATE) {
            return {
                title: t("label.deactivateUser").toUpperCase(),
                sureTxt: t("txt.areYouSureDeactivateAccount"),
                contentToConfirm: t("txt.userID"),
                actionLabel: t("action.deactivate")
            }
        } 
        if (modal.type === Statuses.ACTIVATE) {
            return {
                title: t("label.activateUser").toUpperCase(),
                sureTxt: t("txt.areYouSureActivateAccount"),
                contentToConfirm: t("txt.userID"),
                actionLabel: t("action.activate")
            }
        } 
        return {
            title: '-',
            sureTxt: '-',
            contentToConfirm: '-',
            actionLabel: '-'
        }
    }

    const handleSetModal = (type:Statuses) => {
        setModal({
            ...modal, isOpen:true, record:userDetails, type:type,
        })
    }

    const handleResetModalState = () => {
        setModal(initialModal);
    }

    return (
        <div className="p-20 my-20 user-profile-container">
            <div className="main-header">
                <span className="header-title">
                    {t("title.id").toUpperCase() + " " + userDetails.userRole?.code + ' | '}
                    {
                        userDetails.userRole?.type === AccountRole.COMPANY ?
                            t("title.companyUser").toUpperCase() : 
                            t("title.customerUser").toUpperCase()
                    }
                </span>
                <CloseCircleOutlined onClick={() => { onBack() }}
                    className="cursor-pointer back-action"
                />
            </div>

            <Tabs
                destroyInactiveTabPane={true}
                onTabClick={handleTabClick}
                defaultActiveKey="0"
                activeKey={tabActiveKey}
                tabBarGutter={25}
                tabs={tabs}
                className="mt-30 p-20"
            />

            <Row justify='end'>
                <UserProfileTabFooter
                    canEdit={canEdit}
                    canDelete={canDelete}
                    handleEdit={handleEditUser}
                    handleSetModal={handleSetModal}
                    userDetails={userDetails}
                />
            </Row>

            <BasicModal
                isOpen={modal.isOpen}
                isClosable={true}
                isMaskClosable={false}
                onClose={()=>handleResetModalState()}
            >
                <ModalContent 
                    modal={modal}
                    onHandleModalTranslation={handleModalTranslation}
                    onResetModal={handleResetModalState}
                    onDeactivateOrDelete={onDeactivateUser}
                />
            </BasicModal>
        </div>
    )
}

export default UserProfile